import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Modal } from 'react-bootstrap';
import { getPricing, activateFree } from '../plansApi';

// Shown on the owner dashboard once a business has registered more than one listing but has no
// active subscription or trial: a dismissible nudge to start the free trial period (§8–§10).
// It reappears after each further listing (keyed on the listing count) until the owner starts a
// trial or subscribes. Renders nothing when the plans module is off or the owner already has an
// access-granting subscription.
const DISMISS_KEY = 'rental.trialNudge.dismissedAtCount';
const MIN_LISTINGS = 2;

function readDismissedCount() {
  try {
    return Number(localStorage.getItem(DISMISS_KEY)) || 0;
  } catch {
    return 0;
  }
}

function rememberDismissed(count) {
  try {
    localStorage.setItem(DISMISS_KEY, String(count));
  } catch {
    /* private mode / storage disabled — the nudge just shows again next load */
  }
}

export default function TrialNudgeModal({ listingCount = 0, onActivated }) {
  const [pricing, setPricing] = useState(null);
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');

  useEffect(() => {
    if (listingCount < MIN_LISTINGS || listingCount <= readDismissedCount()) return undefined;
    let alive = true;
    getPricing()
      .then((data) => {
        if (!alive) return;
        // Module off, or the owner already has an active plan / trial — nothing to nudge.
        if (!data?.moduleEnabled || data.owner?.currentStatus) return;
        setPricing(data);
        setShow(true);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [listingCount]);

  if (!show || !pricing) return null;

  const freePlan = pricing.plans.find((p) => p.recommended && p.freeActivation?.enabled)
    || pricing.plans.find((p) => p.freeActivation?.enabled);
  const canTrial = !!freePlan && !pricing.owner?.freeActivationUsed;
  const trialMonths = freePlan ? Math.round(freePlan.freeActivation.durationDays / 30) : 0;

  const dismiss = () => {
    rememberDismissed(listingCount);
    setShow(false);
  };

  const startTrial = async () => {
    setBusy(true);
    setError('');
    try {
      await activateFree(freePlan.code);
      rememberDismissed(listingCount);
      setDone(`Your free ${freePlan.name} trial is active. Enjoy every feature for ${trialMonths} month${trialMonths === 1 ? '' : 's'}.`);
      if (onActivated) onActivated();
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to start the free trial.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show onHide={dismiss} centered>
      <Modal.Header closeButton>
        <Modal.Title>{done ? 'Trial started' : 'Keep every feature as you grow'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {done ? (
          <p className="mb-0">{done}</p>
        ) : (
          <>
            <p>
              You now have {listingCount} listings. Businesses on a plan get higher listing
              limits, richer analytics and priority support.
            </p>
            {canTrial ? (
              <p className="mb-0">
                Start a free {trialMonths}-month trial of <strong>{freePlan.name}</strong> now —
                no payment details needed.
              </p>
            ) : (
              <p className="mb-0">See the plans and pick the one that fits your business.</p>
            )}
            {error && <p className="text-danger small mt-2 mb-0">{error}</p>}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {done ? (
          <Button variant="primary" onClick={() => setShow(false)}>Done</Button>
        ) : (
          <>
            <Button variant="link" className="text-muted" onClick={dismiss}>Maybe later</Button>
            <Button as={Link} to="/pricing" variant="outline-primary" onClick={dismiss}>See all plans</Button>
            {canTrial && (
              <Button variant="primary" disabled={busy} onClick={startTrial}>
                {busy ? 'Starting…' : 'Try for free'}
              </Button>
            )}
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}
